import React, { Fragment, useEffect, useState, useContext } from 'react'
import { Redirect } from 'react-router-dom'
import { useGiftBundles } from 'utils/hooks/useGiftBundles'
import { Loader } from 'components/loader'
import { HeroHeader } from 'components/hero-header'
import { Container, Row, Col } from 'components/grid'
import { BundleCard } from 'components/bundle-card'
import { ByobCallout } from 'components/byob-callout'
import { SectionIntro } from 'components/section-intro'
import { Carousel } from 'components/carousel'
import { Box } from 'components/box'
import { useTrackImpression } from 'utils/hooks/useTrackImpression'
import { isPopular } from 'utils/cardFilters'
import { handleSessionError } from 'utils/error-tracking'
import { MessageFormat } from '@paypalcorp/worldready'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'

const media = {
  lg: 'https://www.paypalobjects.com/digitalassets/c/gifts/us/tiles_d_01.mp4',
  sm: 'https://www.paypalobjects.com/digitalassets/c/gifts/us/tiles_m_01.mp4',
}

const Bundles = () => {
  const {
    allBundles,
    allBundlesLoading,
    miniBundlesLoading,
    miniBundles,
    bundlesError,
  } = useGiftBundles()

  const [firstHalf, setFirstHalf] = useState(null)
  const [secondHalf, setSecondHalf] = useState(null)
  const worldready = useContext(WorldReadyContext)

  const splitBundle = () => {
    let rowTop = 3 //show 3 in the top row
    const sortedBundles = allBundles.sort(
      (a, b) => (isPopular(b) ? 1 : 0) - (isPopular(a) ? 1 : 0),
    )
    let first = sortedBundles.slice(0, rowTop)
    let second = sortedBundles.slice(rowTop, sortedBundles.length)

    setFirstHalf(first)
    setSecondHalf(second)
  }

  useEffect(() => {
    if (!allBundlesLoading) splitBundle()
  }, [allBundlesLoading, miniBundlesLoading])

  useTrackImpression(
    'main:consumer:gifts:bundles:',
    'main:consumer:gifts:bundles::::',
  )

  if (bundlesError) {
    handleSessionError(bundlesError)
    return <Redirect to="/error" />
  }

  const renderCard = (item, i, showDescription = true) => {
    return (
      <BundleCard
        {...item}
        showDescription={showDescription}
        url_key={item.url_key ? item.url_key : '#'}
        data-pagename="main:consumer:gifts:bundles:"
        data-pagename2={`main:consumer:gifts:bundles:${item.url_key}:::`}
      />
    )
  }

  const renderMiniBundlesCarousel = () => {
    if (miniBundlesLoading) {
      return (
        <Container>
          <Loader p="100px 0" />
        </Container>
      )
    }

    let items = miniBundles.map((item, i) => (
      <Box key={`min-bundle-${i}`} height="auto" width="290px">
        {renderCard(item, i, false)}
      </Box>
    ))

    return items.length ? (
      <Fragment>
        <Container>
          <Row>
            <Col>
              <SectionIntro
                my="3"
                title={<Message id="pages/bundles.bundles.minBundlesHeader" />}
                eyebrow={
                  <Message id="pages/bundles.bundles.miniBundlesLabel" />
                }
              />
            </Col>
          </Row>
        </Container>
        <Carousel mb="3" items={items} />
      </Fragment>
    ) : null
  }

  const renderBundles = () => {
    if (allBundlesLoading) {
      return (
        <Container>
          <Loader p="100px 0" />
        </Container>
      )
    }
    return (
      <Fragment>
        <Container>
          <Row>
            {firstHalf &&
              firstHalf.map((item, i) => {
                return (
                  <Col
                    xs="12"
                    sm="6"
                    md="4"
                    key={`bundle-card-top-${i}`}
                    my="3"
                  >
                    {renderCard(item, i)}
                  </Col>
                )
              })}
          </Row>
        </Container>

        {renderMiniBundlesCarousel()}

        <ByobCallout
          my="3"
          title={new MessageFormat(worldready, {
            id: 'pages/bundles.bundles.byobHeader',
          }).format()}
          eyebrow={<Message id="pages/bundles.bundles.byobLabel" />}
          ctaLabel={<Message id="pages/bundles.bundles.byobCta" />}
          ctaHref="/byob"
          name="BYOCTA"
          data-pagename="main:consumer:gifts:bundles:"
          data-pagename2="main:consumer:gifts:bundles:byocta:::"
        />

        <Container>
          <Row>
            {secondHalf &&
              secondHalf.map((item, i) => {
                return (
                  <Col
                    xs="12"
                    sm="6"
                    md="4"
                    key={`bundle-card-bottom-${i}`}
                    mt="3"
                    mb="5"
                  >
                    {renderCard(item, i)}
                  </Col>
                )
              })}
          </Row>
        </Container>
      </Fragment>
    )
  }

  const suggestions = new MessageFormat(worldready, {
    id: 'pages/suggestions.suggestions.all',
  }).format()

  return (
    <section>
      <HeroHeader
        headerStyle="search"
        headline={new MessageFormat(worldready, {
          id: 'pages/bundles.bundles.header',
        }).format()}
        searchSuggestions={suggestions ? suggestions.split(',') : []}
        searchType="bundles"
        mediaSrc={media}
      />

      {renderBundles()}
    </section>
  )
}

export default Bundles
